"use client";

import { ChevronLeft, ChevronRight, RefreshCw, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, EmptyState } from "@nitro/ui";
import {
  createIracingRankingRequestCoordinator,
  formatIracingRankingDate,
  IRACING_RANKING_PAGE_SIZE,
  loadIracingRanking,
  scheduleIracingRankingInitialLoad,
} from "../lib/iracingRankingRequest";

const DEFAULT_CATEGORY = "sports_car";
const PAGE_SIZE = IRACING_RANKING_PAGE_SIZE;
const LIVE_REGION_STYLE = {
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
};

export function IracingLicenseRanking({ initialData = null, loadRanking = loadIracingRanking } = {}) {
  const [data, setData] = useState(initialData);
  const [activeCategory, setActiveCategory] = useState(initialData?.category?.key || DEFAULT_CATEGORY);
  const [page, setPage] = useState(initialData?.pagination?.page || 1);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState("");
  const coordinatorRef = useRef(null);
  const lastIntentRef = useRef({ category: activeCategory, page });
  if (!coordinatorRef.current) coordinatorRef.current = createIracingRankingRequestCoordinator();
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const categories = useMemo(() => (
    Array.isArray(data?.categories) && data.categories.length
      ? data.categories
      : [{ key: DEFAULT_CATEGORY, label: "Sports Car", shortLabel: "Sports", count: rows.length }]
  ), [data, rows.length]);
  const pagination = data?.pagination || { page, limit: PAGE_SIZE, total: rows.length, totalPages: 1 };

  const loadCategory = useCallback(async (nextCategory, nextPage = 1) => {
    const intent = { category: nextCategory, page: nextPage, limit: PAGE_SIZE };
    lastIntentRef.current = intent;
    setActiveCategory(nextCategory);
    setPage(nextPage);
    setLoading(true);
    setError("");
    const result = await coordinatorRef.current.run(loadRanking, intent);
    if (result.status === "cancelled" || result.status === "stale") return;
    if (result.status === "error") {
      setLoading(false);
      setError(result.error?.message || "No se pudo cargar el ranking iRacing.");
      return;
    }
    const nextData = result.data;
    setData(nextData);
    setActiveCategory(nextData.category?.key || nextCategory);
    setPage(nextData.pagination?.page || nextPage);
    setLoading(false);
  }, [loadRanking]);

  useEffect(() => {
    if (initialData) return undefined;
    const cancelScheduledLoad = scheduleIracingRankingInitialLoad(() => {
      void loadCategory(DEFAULT_CATEGORY, 1);
    });
    return () => {
      cancelScheduledLoad();
      coordinatorRef.current?.cancel("initial-load-effect-cleanup");
    };
  }, [initialData, loadCategory]);

  useEffect(() => () => {
    coordinatorRef.current?.cancel("unmounted");
  }, []);

  const selectedCategoryLabel = categories.find((category) => category.key === activeCategory)?.label || activeCategory;
  const liveMessage = loading
    ? `Cargando ranking ${selectedCategoryLabel}, página ${page}.`
    : error
      ? `No se pudo actualizar el ranking ${selectedCategoryLabel}.`
      : `Ranking ${data?.category?.label || selectedCategoryLabel} actualizado. Página ${pagination.page || page} de ${pagination.totalPages || 1}.`;

  return (
    <section className="simulator-hub-panel iracing-ranking-panel" id="ranking-iracing">
      <p aria-live="polite" role="status" style={LIVE_REGION_STYLE}>{liveMessage}</p>
      <div className="simulator-hub-panel-head">
        <div>
          <p className="nitro-eyebrow">Ranking iRacing</p>
          <h2>Licencias de pilotos registrados</h2>
          <p>Datos publicos sincronizados desde iRacing para pilotos Nitro con cuenta iRacing registrada.</p>
        </div>
        <div className="iracing-ranking-summary" aria-label="Resumen del ranking">
          <ShieldCheck size={18} />
          <strong>{formatNumber(pagination.total)}</strong>
          <span>{data?.category?.label || "Sports Car"}</span>
        </div>
      </div>

      <div className="iracing-license-tabs" aria-label="Tipos de licencia iRacing">
        {categories.map((category) => (
          <button
            aria-pressed={category.key === activeCategory}
            className={category.key === activeCategory ? "is-active" : ""}
            key={category.key}
            onClick={() => loadCategory(category.key, 1)}
            style={{ minHeight: 44 }}
            type="button"
          >
            <span>{category.shortLabel || category.label}</span>
            <strong>{formatNumber(category.count)}</strong>
          </button>
        ))}
      </div>

      {error ? <Alert tone="warning" title="No pudimos actualizar el ranking" actions={<Button iconStart={<RefreshCw size={16} />} loading={loading} variant="secondary" onClick={() => loadCategory(lastIntentRef.current.category, lastIntentRef.current.page)}>Reintentar</Button>}>{error}{data ? " Sigues viendo la última consulta correcta." : ""}</Alert> : null}

      <div className="iracing-ranking-table-wrap" aria-busy={loading}>
        <table className="iracing-ranking-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Nombre para mostrar</th>
              <th scope="col">País</th>
              <th scope="col">iRating</th>
              <th scope="col">Licencia</th>
              <th scope="col">Safety</th>
              <th scope="col">Actualizado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.category}-${row.custId}`}>
                <td data-label="#">{row.position}</td>
                <td data-label="Nombre para mostrar">
                  <a href={row.pilotSlug ? `/pilotos/${row.pilotSlug}` : "#ranking-iracing"}>{row.displayName}</a>
                </td>
                <td data-label="Pais">
                  <strong>{row.country || "Pais pendiente"}</strong>
                  {row.city ? <span>{row.city}</span> : null}
                  {!row.city && row.countrySource === "iracing" ? <span>Bandera iRacing</span> : null}
                </td>
                <td data-label="iRating">{row.irating ? formatNumber(row.irating) : "-"}</td>
                <td data-label="Licencia"><em>{row.license || "Sin licencia"}</em></td>
                <td data-label="Safety">{row.safetyRating !== null && row.safetyRating !== undefined ? Number(row.safetyRating).toFixed(2) : "-"}</td>
                <td data-label="Actualizado">{formatIracingRankingDate(row.syncedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && !error && !loading ? <EmptyState title="No hay licencias sincronizadas" description="Las licencias aparecerán cuando existan datos públicos sincronizados para esta categoría." /> : null}
      </div>

      <footer className="iracing-ranking-footer">
        <span>
          Pagina {pagination.page || page} de {pagination.totalPages || 1}
          {loading ? " / actualizando..." : ""}
        </span>
        <div>
          <button
            disabled={loading || (pagination.page || page) <= 1}
            onClick={() => loadCategory(activeCategory, Math.max(1, (pagination.page || page) - 1))}
            style={{ minHeight: 44 }}
            type="button"
          >
            <ChevronLeft size={16} />
            Anterior
          </button>
          <button
            disabled={loading || (pagination.page || page) >= (pagination.totalPages || 1)}
            onClick={() => loadCategory(activeCategory, (pagination.page || page) + 1)}
            style={{ minHeight: 44 }}
            type="button"
          >
            Siguiente
            <ChevronRight size={16} />
          </button>
        </div>
      </footer>
    </section>
  );
}

function formatNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("es-CO").format(number) : "0";
}

